import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Group, SegmentedControl } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";

import { useResponsiveDatePickerProps, SelectField } from "../form";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import isoWeek from "dayjs/plugin/isoWeek";

import { AppButton } from "../AppButton";
import { AppIcon } from "../AppIcon";

// Enable dayjs plugins
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

type ViewMode = "day" | "week" | "month";

export interface DateRangeValue {
  startDate: string;
  endDate: string;
  viewMode: ViewMode;
  displayDate: string;
}

export interface DateRangeSelectorProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  disabled?: boolean;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  size = "md",
}) => {
  const responsiveDatePicker = useResponsiveDatePickerProps();
  // Internal state for date inputs
  const [selectedDate, setSelectedDate] = useState<string>(
    value.startDate || dayjs().format("YYYY-MM-DD")
  );
  const [selectedMonth, setSelectedMonth] = useState<string>(
    dayjs(value.startDate || dayjs())
      .month()
      .toString()
  );
  const [selectedYear, setSelectedYear] = useState<string>(
    dayjs(value.startDate || dayjs())
      .year()
      .toString()
  );

  // Build date range based on view mode and selected values
  const buildDateRange = useCallback(
    (
      viewMode: ViewMode,
      selectedDate: string,
      selectedMonth: string,
      selectedYear: string
    ): DateRangeValue => {
      switch (viewMode) {
        case "day": {
          const date = dayjs(selectedDate);
          return {
            viewMode,
            startDate: date.format("YYYY-MM-DD"),
            endDate: date.format("YYYY-MM-DD"),
            displayDate: date.format("MMMM DD, YYYY"),
          };
        }

        case "week": {
          const date = dayjs(selectedDate);
          const weekStart = date.startOf("isoWeek");
          const weekEnd = date.endOf("isoWeek");
          return {
            viewMode,
            startDate: weekStart.format("YYYY-MM-DD"),
            endDate: weekEnd.format("YYYY-MM-DD"),
            displayDate: `${weekStart.format("MMM DD")} - ${weekEnd.format(
              "MMM DD, YYYY"
            )}`,
          };
        }

        case "month": {
          const monthDate = dayjs()
            .month(parseInt(selectedMonth))
            .year(parseInt(selectedYear));
          const monthStart = monthDate.startOf("month");
          const monthEnd = monthDate.endOf("month");
          return {
            viewMode,
            startDate: monthStart.format("YYYY-MM-DD"),
            endDate: monthEnd.format("YYYY-MM-DD"),
            displayDate: monthDate.format("MMMM YYYY"),
          };
        }

        default: {
          const date = dayjs(selectedDate);
          return {
            viewMode,
            startDate: date.format("YYYY-MM-DD"),
            endDate: date.format("YYYY-MM-DD"),
            displayDate: date.format("MMMM DD, YYYY"),
          };
        }
      }
    },
    []
  );

  // Update parent when internal state changes
  useEffect(() => {
    const newValue = buildDateRange(
      value.viewMode,
      selectedDate,
      selectedMonth,
      selectedYear
    );

    // Only call onChange if the value actually changed
    if (
      newValue.startDate !== value.startDate ||
      newValue.endDate !== value.endDate ||
      newValue.displayDate !== value.displayDate
    ) {
      onChange(newValue);
    }
  }, [
    value.viewMode,
    selectedDate,
    selectedMonth,
    selectedYear,
    buildDateRange,
    onChange,
    value.startDate,
    value.endDate,
    value.displayDate,
  ]);

  // Sync internal state when external value changes
  useEffect(() => {
    if (value.startDate) {
      const date = dayjs(value.startDate);
      setSelectedDate(value.startDate);
      setSelectedMonth(date.month().toString());
      setSelectedYear(date.year().toString());
    }
  }, [value.startDate]);

  // Generate year options (previous year to 2 years from current)
  const yearOptions = useMemo(() => {
    const currentYear = dayjs().year();
    const years = [];
    for (let year = currentYear - 1; year <= currentYear + 2; year++) {
      years.push({
        value: year.toString(),
        label: year.toString(),
      });
    }
    return years;
  }, []);

  // Generate month options
  const monthOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      value: i.toString(),
      label: dayjs().month(i).format("MMMM"),
    }));
  }, []);

  // Handle view mode change
  const handleViewModeChange = useCallback(
    (newViewMode: string) => {
      const newValue = buildDateRange(
        newViewMode as ViewMode,
        selectedDate,
        selectedMonth,
        selectedYear
      );
      onChange(newValue);
    },
    [selectedDate, selectedMonth, selectedYear, buildDateRange, onChange]
  );

  // Handle date change for day/week views
  const handleDateChange = useCallback((newDate: string | null) => {
    if (newDate) {
      setSelectedDate(newDate);
    }
  }, []);

  // Handle month change for month view
  const handleMonthChange = useCallback((month: string | null) => {
    if (month !== null) {
      setSelectedMonth(month);
    }
  }, []);

  // Handle year change for month view
  const handleYearChange = useCallback((year: string | null) => {
    if (year !== null) {
      setSelectedYear(year);
    }
  }, []);

  // Handle navigation to previous period
  const handlePreviousPeriod = useCallback(() => {
    if (value.viewMode === "day") {
      const prevDay = dayjs(selectedDate)
        .subtract(1, "day")
        .format("YYYY-MM-DD");
      setSelectedDate(prevDay);
    } else if (value.viewMode === "week") {
      const prevWeek = dayjs(selectedDate)
        .subtract(1, "week")
        .format("YYYY-MM-DD");
      setSelectedDate(prevWeek);
    }
  }, [value.viewMode, selectedDate]);

  // Handle navigation to next period
  const handleNextPeriod = useCallback(() => {
    if (value.viewMode === "day") {
      const nextDay = dayjs(selectedDate).add(1, "day").format("YYYY-MM-DD");
      setSelectedDate(nextDay);
    } else if (value.viewMode === "week") {
      const nextWeek = dayjs(selectedDate).add(1, "week").format("YYYY-MM-DD");
      setSelectedDate(nextWeek);
    }
  }, [value.viewMode, selectedDate]);

  return (
    <Group gap="sm">
      <SegmentedControl
        value={value.viewMode}
        onChange={handleViewModeChange}
        data={[
          { label: "Day", value: "day" },
          { label: "Week", value: "week" },
          { label: "Month", value: "month" },
        ]}
        size={size}
        disabled={disabled}
      />

      {value.viewMode !== "month" ? (
        <Group gap="xs">
          <AppButton
            variant="secondary"
            size={size}
            onClick={handlePreviousPeriod}
            disabled={disabled}
            style={{ minWidth: 32 }}
          >
            <AppIcon iconName="ChevronLeft" size={16} />
          </AppButton>
          <DatePickerInput
            placeholder="Select date"
            value={selectedDate}
            onChange={handleDateChange}
            size={size}
            style={{ minWidth: 160 }}
            disabled={disabled}
            {...responsiveDatePicker}
          />
          <AppButton
            variant="secondary"
            size={size}
            onClick={handleNextPeriod}
            disabled={disabled}
            style={{ minWidth: 32 }}
          >
            <AppIcon iconName="ChevronRight" size={16} />
          </AppButton>
        </Group>
      ) : (
        <>
          <SelectField
            placeholder="Month"
            value={selectedMonth}
            onChange={(_name, v) => handleMonthChange(v)}
            data={monthOptions}
            size={size}
            style={{ minWidth: 120 }}
            disabled={disabled}
          />
          <SelectField
            placeholder="Year"
            value={selectedYear}
            onChange={(_name, v) => handleYearChange(v)}
            data={yearOptions}
            size={size}
            style={{ minWidth: 100, width: 100 }}
            disabled={disabled}
          />
        </>
      )}
    </Group>
  );
};
